import { apiClient, ApiClient } from "@/lib/api/api-client";
import {
  BusinessBaseInfo,
  BusinessInfo,
} from "@/modules/types/business/business";
import { BaseResponse } from "@/modules/types/response";
import { Industry } from "@/modules/types/metadata/metadata";
import { Review, ReviewSummary, Criteria } from "@/modules/types/business/review";

export interface ReviewResponse extends BaseResponse {
  reviews?: Review[];
}

export interface ReviewSummaryResponse extends BaseResponse {
  summary?: ReviewSummary;
}

interface BusinessResp extends BaseResponse {
  businessInfos?: BusinessBaseInfo[];
  total: number;
  nextId?: string;
}

interface BusinessDetailResp extends BaseResponse {
  businessInfo?: BusinessInfo;
}

export interface IndustryResponse extends BaseResponse {
  industry?: Industry[];
}

export interface VerifyInvitationResponse extends BaseResponse {
  isRedirect: boolean;
  businessId: string;
  email: string;
}

export class BusinessService {
  private BASE_PATH =
    process.env.API_BUSINESS_ENDPOINT || "http://localhost:8081/business";
  private apiClient: ApiClient = apiClient;
  private apiVersion = "v1.0.0";
  constructor(apiVersion?: string) {
    this.apiVersion = apiVersion || this.apiVersion;
  }

  async getIndustries(): Promise<IndustryResponse> {
    return await this.apiClient.fetch<IndustryResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/industries`,
    );
  }

  async getCompanies({
    search,
    nextId,
    province,
    industryId,
    limit,
    offset,
  }: {
    search?: string;
    nextId?: string;
    province?: string;
    industryId?: string;
    limit?: number;
    offset?: number;
  }): Promise<BusinessResp | undefined> {
    const queryParam = new URLSearchParams();
    if (search) {
      queryParam.set("businessName", search);
    }
    if (nextId) {
      queryParam.set("nextId", nextId);
    }
    if (limit) {
      queryParam.set("limit", limit.toString());
    }
    if (offset) {
      queryParam.set("offset", offset.toString());
    }
    if (province) {
      queryParam.set("province", province);
    }
    if (industryId) {
      queryParam.set("industryId", industryId);
    }
    return this.apiClient.fetch<BusinessResp>(
      `${this.BASE_PATH}/${this.apiVersion}/search?${queryParam.toString()}`,
    );
  }

  async searchCompanies({
    search,
    nextId,
    limit,
    offset,
  }: {
    search?: string;
    nextId?: string;
    limit?: number;
    offset?: number;
  }): Promise<BusinessResp | undefined> {
    const queryParam = new URLSearchParams();
    if (search) {
      queryParam.set("businessName", search);
    }
    if (nextId) {
      queryParam.set("nextId", nextId);
    }
    if (limit) {
      queryParam.set("limit", limit.toString());
    }
    if (offset) {
      queryParam.set("offset", offset.toString());
    }
    return this.apiClient.fetch<BusinessResp>(
      `${this.BASE_PATH}/${this.apiVersion}/search?${queryParam.toString()}`,
    );
  }

  async getCompanyBySlug(
    slug: string,
  ): Promise<BusinessDetailResp | undefined> {
    return this.apiClient.fetch<BusinessDetailResp>(
      `${this.BASE_PATH}/${this.apiVersion}/slug/${slug}`,
    );
  }

  async acceptInvitation(
    id: string,
    token: string,
  ): Promise<VerifyInvitationResponse> {
    return this.apiClient.fetch<VerifyInvitationResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/verify-invitation`,
      {
        method: "POST",
        body: JSON.stringify({ invitationId: id, token }),
      },
    );
  }

  async getReviews(businessId: string): Promise<ReviewResponse | undefined> {
    return this.apiClient.fetch<ReviewResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/reviews/${businessId}`,
    );
  }

  async getReviewSummary(businessId: string): Promise<ReviewSummaryResponse | undefined> {
    return this.apiClient.fetch<ReviewSummaryResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/reviews/summary/${businessId}`,
    );
  }

  async createReview(data: {
    businessId: string;
    review: string;
    starRating: number;
    criterias: Criteria[];
  }): Promise<BaseResponse | undefined> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/reviews`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  }
}

export const businessService = new BusinessService();
